import { 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  increment, 
  serverTimestamp,
  runTransaction 
} from 'firebase/firestore';
import { db } from './config';

// Point values for different activities
export const REPUTATION_POINTS = {
  SITE_SUBMISSION: 5,
  MEDIA_UPLOAD: 1,
  COMMENT_UPVOTE: 1
};

// Activity types for tracking
export const ACTIVITY_TYPES = {
  SITE_SUBMISSION: 'site_submission',
  MEDIA_UPLOAD: 'media_upload',
  COMMENT_UPVOTE: 'comment_upvote'
};

/**
 * Initialize user reputation document if it doesn't exist
 */
const initializeUserReputation = async (userId) => {
  const userReputationRef = doc(db, 'user_reputation', userId);
  const userReputationDoc = await getDoc(userReputationRef);
  
  if (!userReputationDoc.exists()) {
    const initialData = {
      userId,
      totalPoints: 0,
      activities: {
        sitesSubmitted: 0,
        mediaUploaded: 0,
        commentUpvotes: 0
      },
      pointsBreakdown: {
        fromSites: 0,
        fromMedia: 0,
        fromUpvotes: 0
      },
      createdAt: serverTimestamp(),
      lastUpdated: serverTimestamp()
    };
    
    await setDoc(userReputationRef, initialData);
  }
};

/**
 * Update user reputation for a specific activity
 */
export const updateUserReputation = async (userId, activityType, count = 1) => {
  try {
    if (!userId || !activityType) {
      return { success: false, error: 'Missing required parameters' };
    }

    // Ensure user reputation document exists
    await initializeUserReputation(userId);

    const userReputationRef = doc(db, 'user_reputation', userId);
    
    // Use transaction to ensure atomic updates
    await runTransaction(db, async (transaction) => {
      const userReputationDoc = await transaction.get(userReputationRef);
      
      if (!userReputationDoc.exists()) {
        throw new Error('User reputation document not found');
      }

      const currentData = userReputationDoc.data();
      let pointsToAdd = 0;
      let activityUpdate = {};
      let breakdownUpdate = {};

      switch (activityType) {
        case ACTIVITY_TYPES.SITE_SUBMISSION:
          pointsToAdd = REPUTATION_POINTS.SITE_SUBMISSION * count;
          activityUpdate = { 'activities.sitesSubmitted': increment(count) };
          breakdownUpdate = { 'pointsBreakdown.fromSites': increment(pointsToAdd) };
          break;
          
        case ACTIVITY_TYPES.MEDIA_UPLOAD:
          pointsToAdd = REPUTATION_POINTS.MEDIA_UPLOAD * count;
          activityUpdate = { 'activities.mediaUploaded': increment(count) };
          breakdownUpdate = { 'pointsBreakdown.fromMedia': increment(pointsToAdd) };
          break;
          
        case ACTIVITY_TYPES.COMMENT_UPVOTE:
          pointsToAdd = REPUTATION_POINTS.COMMENT_UPVOTE * count;
          activityUpdate = { 'activities.commentUpvotes': increment(count) };
          breakdownUpdate = { 'pointsBreakdown.fromUpvotes': increment(pointsToAdd) };
          break;
          
        default:
          throw new Error(`Unknown activity type: ${activityType}`);
      }

      // Update the document
      transaction.update(userReputationRef, {
        totalPoints: increment(pointsToAdd),
        ...activityUpdate,
        ...breakdownUpdate,
        lastUpdated: serverTimestamp()
      });
    });

    return { success: true, pointsAdded: pointsToAdd };
  } catch (error) {
    console.error('Error updating user reputation:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get user reputation data
 */
export const getUserReputationData = async (userId) => {
  try {
    if (!userId) {
      return { success: false, error: 'User ID is required' };
    }

    const userReputationRef = doc(db, 'user_reputation', userId);
    const userReputationDoc = await getDoc(userReputationRef);
    
    if (!userReputationDoc.exists()) {
      // Initialize if doesn't exist
      await initializeUserReputation(userId);
      
      // Return initial data
      return {
        success: true,
        data: {
          totalPoints: 0,
          activities: {
            sitesSubmitted: 0,
            mediaUploaded: 0,
            commentUpvotes: 0
          },
          pointsBreakdown: {
            fromSites: 0,
            fromMedia: 0,
            fromUpvotes: 0
          }
        }
      };
    }

    return {
      success: true,
      data: userReputationDoc.data()
    };
  } catch (error) {
    console.error('Error getting user reputation:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Calculate total reputation points (legacy function for backward compatibility)
 */
export const calculateTotalReputation = async (userId) => {
  try {
    const result = await getUserReputationData(userId);
    if (result.success) {
      return { success: true, data: result.data.totalPoints };
    }
    return result;
  } catch (error) {
    console.error('Error calculating total reputation:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Batch update reputation for multiple activities
 */
export const batchUpdateReputation = async (userId, activities) => {
  try {
    if (!userId || !Array.isArray(activities) || activities.length === 0) {
      return { success: false, error: 'Invalid parameters' };
    }

    let totalPointsAdded = 0;
    
    for (const activity of activities) {
      const result = await updateUserReputation(userId, activity.type, activity.count || 1);
      if (result.success) {
        totalPointsAdded += result.pointsAdded;
      }
    }

    return { success: true, totalPointsAdded };
  } catch (error) {
    console.error('Error batch updating reputation:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get reputation level based on points
 */
export const getReputationLevel = (totalPoints) => {
  if (totalPoints >= 1000) return { level: 'Expert', color: 'ancient.gold' };
  if (totalPoints >= 500) return { level: 'Advanced', color: 'ancient.terracotta' };
  if (totalPoints >= 100) return { level: 'Contributor', color: 'ancient.stone' };
  if (totalPoints >= 25) return { level: 'Explorer', color: 'text.muted' };
  return { level: 'Novice', color: 'text.default' };
};

/**
 * Format reputation breakdown for display
 */
export const formatReputationBreakdown = (reputationData) => {
  if (!reputationData) return '';
  
  const { activities } = reputationData;
  const parts = [];
  
  if (activities.sitesSubmitted > 0) {
    parts.push(`${activities.sitesSubmitted} site${activities.sitesSubmitted !== 1 ? 's' : ''}`);
  }
  
  if (activities.mediaUploaded > 0) {
    parts.push(`${activities.mediaUploaded} media upload${activities.mediaUploaded !== 1 ? 's' : ''}`);
  }
  
  if (activities.commentUpvotes > 0) {
    parts.push(`${activities.commentUpvotes} upvote${activities.commentUpvotes !== 1 ? 's' : ''}`);
  }
  
  if (parts.length === 0) return 'No activity yet';
  if (parts.length === 1) return `From ${parts[0]}`;
  if (parts.length === 2) return `From ${parts[0]} and ${parts[1]}`;
  return `From ${parts.slice(0, -1).join(', ')}, and ${parts[parts.length - 1]}`;
};
